import { createContext, createElement, useContext, useLayoutEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type Direction = 'ltr' | 'rtl';

// RTL languages. Seeded with the known RTL catalog codes so direction is right
// before the catalog loads; refined at runtime from GET /i18n/languages
// (catalog.isRtl) via setRtlLocales, so a newly-added RTL language flips
// direction with no code change.
let rtlSet = new Set<string>(['ar', 'ckb', 'kmr', 'fa', 'he', 'ur']);

export function directionFor(locale: string): Direction {
  const primary = locale.split('-')[0] ?? '';
  return rtlSet.has(primary) ? 'rtl' : 'ltr';
}

/** Replace the RTL language set (from the language catalog) + re-apply <html dir>. */
export function setRtlLocales(rtlCodes: string[]): void {
  rtlSet = new Set(rtlCodes);
  if (typeof document !== 'undefined') {
    const lng = document.documentElement.getAttribute('lang') ?? 'en';
    document.documentElement.setAttribute('dir', directionFor(lng));
  }
}

/**
 * Returns the active text direction. Re-renders when the i18n language changes,
 * and keeps `<html lang>` + `<html dir>` in sync as a side effect.
 */
export function useDirection(): Direction {
  const { i18n } = useTranslation();
  const [dir, setDir] = useState<Direction>(() => directionFor(i18n.language ?? 'en'));

  // useLayoutEffect (not useEffect) so <html lang>/<html dir> are synced BEFORE
  // the first paint — otherwise an Arabic-first load briefly flashes LTR.
  useLayoutEffect(() => {
    const onChange = (lng: string): void => {
      const next = directionFor(lng);
      setDir(next);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', lng.split('-')[0] ?? 'en');
        document.documentElement.setAttribute('dir', next);
      }
    };
    onChange(i18n.language ?? 'en');
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, [i18n]);

  return dir;
}

/*
 * The direction a PAGE is laid out in, which is not always the language's.
 *
 * By default a page follows the language, like the shell. A page that must stay
 * one way (the add/edit product form is left-to-right in Arabic and Kurdish)
 * wraps itself in <DirectionScope dir="ltr"> plus a matching `dir` attribute.
 * Anything that picks a direction for itself (a dialog, which portals out to
 * <body> and would inherit <html dir>; Radix Tabs; a chevron that points
 * "forward") asks useLayoutDirection() rather than the language, so it follows
 * the scope. Outside a scope it is the language's direction.
 */
const ScopeContext = createContext<Direction | null>(null);

export function DirectionScope({ dir, children }: { dir: Direction; children: ReactNode }): JSX.Element {
  return createElement(ScopeContext.Provider, { value: dir }, children);
}

/** The direction to lay content out in here: the enclosing DirectionScope's, else the language's. */
export function useLayoutDirection(): Direction {
  const scoped = useContext(ScopeContext);
  const { i18n } = useTranslation();
  return scoped ?? directionFor(i18n.language ?? 'en');
}

// The confirm dialog is mounted once at the root, outside every page, yet it is
// always asked for BY a page — so it cannot learn the page's direction from the
// tree. AppLayout publishes it here while it is mounted.
let pageDir: Direction | null = null;
const pageDirListeners = new Set<() => void>();

export function setPageDirection(dir: Direction | null): void {
  if (pageDir === dir) return;
  pageDir = dir;
  pageDirListeners.forEach((l) => l());
}

/** For overlays mounted outside the pages: the pages' direction while the admin shell is up, else the language's. */
export function usePageDirection(): Direction {
  const published = useSyncExternalStore(
    (l) => { pageDirListeners.add(l); return () => { pageDirListeners.delete(l); }; },
    () => pageDir,
    () => null,
  );
  const { i18n } = useTranslation();
  return published ?? directionFor(i18n.language ?? 'en');
}

/** Switch the active locale at runtime; emits languageChanged so all subscribers update. */
export async function setLocale(locale: string): Promise<void> {
  const { default: i18n } = await import('i18next');
  await i18n.changeLanguage(locale);
}
